import { Injectable, signal } from '@angular/core';
import { AuthApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/auth/auth.api';
import { SubscriptionApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/subscription/subscription-api';
import { SubscriptionResponse } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/subscription/subscription.response';

/** Clé locale servant à détecter le passage premium → gratuit. */
const LAST_PREMIUM_KEY = 'vw-last-premium';

/** Durée maximale d'attente de l'activation après un paiement. */
const ACTIVATION_TIMEOUT_MS = 30_000;
const ACTIVATION_POLL_MS = 3_000;

/**
 * État premium de l'utilisateur, côté app.
 *
 * Deux sources, à ne pas confondre :
 * - le CLAIM du jeton (`authApi.isPremium()`) : instantané, gratuit, mais il
 *   peut retarder d'une heure après un achat ou une résiliation. Bon pour
 *   choisir un libellé ou masquer un encart.
 * - l'API (`subscriptionApi.me()`) : la vérité, avec l'échéance et le canal.
 *
 * Aucune décision de sécurité ne se prend ici : le serveur refuse de lui-même
 * ce qui doit l'être. Ce service sert l'AFFICHAGE.
 */
@Injectable({ providedIn: 'root' })
export class PremiumService {
  // Les classes du SDK lisent leur configuration dans un singleton : on les
  // instancie directement, comme partout ailleurs dans l'app (AuthApi n'est pas
  // fourni au niveau racine).
  private readonly authApi = new AuthApi();
  private readonly subscriptionApi = new SubscriptionApi();

  readonly isPremium = signal<boolean>(false);
  readonly subscription = signal<SubscriptionResponse | null>(null);
  /** Une transition premium → gratuit vient d'être constatée. */
  readonly justDowngraded = signal<boolean>(false);
  /**
   * Une transition gratuit → premium vient d'être constatée.
   *
   * Porté par le service et non par l'écran d'achat : le paiement se conclut
   * dans le navigateur, et l'essai peut être lancé depuis plusieurs endroits.
   * La célébration doit se déclencher d'où que vienne l'activation.
   */
  readonly justUpgraded = signal<boolean>(false);

  /**
   * L'état premium est-il connu ? Vrai dès la construction : le jeton répond.
   *
   * Conservé pour les écrans qui préfèrent ne rien afficher plutôt qu'une
   * offre payante prématurée — mais il n'y a plus de fenêtre d'incertitude.
   */
  readonly ready = signal<boolean>(false);

  constructor() {
    // Le JETON répond tout de suite : le groupe `PREMIUM_USER` y figure, et
    // `decodeToken()` est synchrone — c'est ainsi que le reste de l'app lit
    // l'authentification (cf. `isGuest` du chatbot et du paywall).
    // Aucune raison de faire attendre l'interface pour ça.
    this.isPremium.set(this.authApi.isPremium());
    this.ready.set(true);

    // L'API corrige ensuite, en arrière-plan. Elle est indispensable au
    // lendemain d'un achat : le jeton en main ne porte pas encore le groupe et
    // ne le portera qu'à son renouvellement, jusqu'à une heure plus tard.
    void this.refresh();
  }

  /**
   * Relit le jeton puis l'état serveur.
   *
   * Appelé une fois le jeton hydraté depuis le stockage natif : au démarrage à
   * froid d'un WebView, il n'était pas encore en place à la construction.
   */
  async init(): Promise<void> {
    this.isPremium.set(this.authApi.isPremium());
    await this.refresh();
  }

  /**
   * Relit l'état auprès du serveur (sans toucher au jeton).
   *
   * @returns true si le serveur a répondu — donc si l'état est FIABLE. Un
   * échec laisse la valeur précédente : ne pas connaître l'abonnement ne doit
   * jamais empêcher d'utiliser l'app.
   */
  async refresh(): Promise<boolean> {
    try {
      const state = await this.subscriptionApi.me();
      this.subscription.set(state);
      this.applyPremiumState(state.isPremium);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Force le renouvellement du jeton puis relit l'état.
   *
   * À appeler au RETOUR d'un achat : l'abonnement existe côté serveur, mais le
   * jeton en main ne porte pas encore le groupe premium. Sans cela,
   * l'utilisateur aurait payé et ne verrait rien changer pendant une heure.
   */
  async refreshEntitlement(): Promise<void> {
    try {
      await this.authApi.forceRefreshToken();
    } catch {
      // Le rafraîchissement peut échouer (réseau) : l'API reste la vérité.
    }
    await this.refresh();
  }

  /**
   * Attend l'activation après un paiement. Le webhook Stripe met quelques
   * secondes à nous parvenir : on interroge jusqu'à ce que le premium arrive,
   * sans jamais bloquer indéfiniment.
   *
   * @returns true si le premium est actif dans le délai imparti.
   */
  async awaitActivation(): Promise<boolean> {
    const deadline = Date.now() + ACTIVATION_TIMEOUT_MS;
    while (Date.now() < deadline) {
      await this.refreshEntitlement();
      if (this.isPremium()) return true;
      await new Promise((resolve) => setTimeout(resolve, ACTIVATION_POLL_MS));
    }
    return this.isPremium();
  }

  /** Le récapitulatif de downgrade a été montré. */
  acknowledgeDowngrade(): void {
    this.justDowngraded.set(false);
  }

  /** La célébration a été montrée. */
  acknowledgeUpgrade(): void {
    this.justUpgraded.set(false);
  }

  /**
   * Met à jour l'état et détecte la transition premium → gratuit.
   *
   * Subtilité : quand la clé locale est ABSENTE (première ouverture, nouvel
   * appareil, cache vidé), on l'initialise sans rien annoncer. Sans cette
   * précaution, tout nouvel utilisateur gratuit verrait un écran « voici ce que
   * tu perds » alors qu'il n'a jamais rien eu — et un abonné de longue date
   * qui change de téléphone serait accueilli par des confettis.
   */
  private applyPremiumState(isPremium: boolean): void {
    const stored = localStorage.getItem(LAST_PREMIUM_KEY);
    const previous = stored === null ? null : stored === 'true';

    if (previous === true && !isPremium) {
      this.justDowngraded.set(true);
    }
    if (previous === false && isPremium) {
      this.justUpgraded.set(true);
    }

    this.isPremium.set(isPremium);
    localStorage.setItem(LAST_PREMIUM_KEY, String(isPremium));
  }
}
